import { DailySunlightResult } from '@/types/sunlight';
import { Select } from '../UI/Select/Select';

interface MetricSelectorProps {
	onMetricChange: (metric: keyof DailySunlightResult) => void;
	currentMetric?: keyof DailySunlightResult;
	disabled?: boolean;
}

interface MetricGroup {
	title: string;
	metrics: Array<{
		key: keyof DailySunlightResult;
		label: string;
		description?: string;
	}>;
}

export const MetricSelector = ({
	onMetricChange,
	currentMetric,
	disabled,
}: MetricSelectorProps) => {
	const metricGroups: MetricGroup[] = [
		{
			title: 'Primary Metrics',
			metrics: [
				{
					key: 'averageDailySunlightHours',
					label: 'Average Daily Hours',
					description: 'Average sunlight hours per day',
				},
				{
					key: 'consistencyScore',
					label: 'Consistency',
					description: 'How consistent sunlight is throughout the year',
				},
				{
					key: 'daylightAutonomyPercent',
					label: 'Daylight Autonomy',
					description: '% of working hours with sunlight',
				},
			],
		},
		{
			title: 'Seasonal Analysis',
			metrics: [
				{ key: 'winterMonthsAverage', label: 'Winter (Dec-Feb)' },
				{ key: 'springMonthsAverage', label: 'Spring (Mar-May)' },
				{ key: 'summerMonthsAverage', label: 'Summer (Jun-Aug)' },
				{ key: 'fallMonthsAverage', label: 'Fall (Sep-Nov)' },
			],
		},
		{
			title: 'Daily Pattern',
			metrics: [
				{ key: 'morningHoursPercent', label: 'Morning %' },
				{ key: 'afternoonHoursPercent', label: 'Afternoon %' },
				{ key: 'midDayHoursAverage', label: 'Midday (10am-2pm)' },
			],
		},
		{
			title: 'Directional & Glare',
			metrics: [
				{ key: 'directionalBalance', label: 'Directional Balance' },
				{
					key: 'glareRiskHours',
					label: 'Glare Risk',
					description: 'Hours/day with low-angle sun',
				},
			],
		},
		{
			title: 'Range & Viability',
			metrics: [
				{ key: 'longestSunlightHours', label: 'Best Day' },
				{ key: 'shortestSunlightHours', label: 'Worst Day' },
				{ key: 'minViableDaysCount', label: 'Good Days (>4h)' },
				{ key: 'daysWithSunlight', label: 'Sunny Days' },
				{ key: 'zeroDaysCount', label: 'No Sun Days' },
			],
		},
	];
	const getMetricOptions = () => {
		const options: { label: string; value: keyof DailySunlightResult }[] = [];
		metricGroups.forEach((group) => {
			group.metrics.forEach((metric) => {
				options.push({ label: metric.label, value: metric.key });
			});
		});
		return options;
	};
	return (
		<div>
			<Select
				disabled={disabled}
				options={getMetricOptions()}
				value={currentMetric}
				onChange={(event) =>
					onMetricChange(event.detail.value as keyof DailySunlightResult)
				}
			/>
		</div>
	);
};
